import classNames from 'classnames'
import { useState } from 'react'
import { useLinkFormatter, useTheme } from '@opallabs/hooks'
import { Link } from '@opallabs/lapidary'
import { EditUrlPopover } from './EditUrlPopover'
import * as styles from './styles'

const OpenLink = ({ url }: { url: string }) => {
  const theme = useTheme()
  const formattedUrl = useLinkFormatter(url)

  return (
    <span css={styles.openUrlContainer}>
      <Link
        href={formattedUrl}
        styleOverrides={styles.openUrlLink(theme)}
        target="_blank"
      >
        Open
      </Link>
    </span>
  )
}

export type URLInputProps = {
  className?: string
  /**
   * `disabled` prop renders popover component with button
   * in disabled state. `readOnly` renders a text only display
   * of the input value
   */
  disabled?: boolean
  htmlId?: string
  initialLabel?: string | null
  initialUrl?: string
  onCancel?: () => void
  onOpenChange?: (open: boolean) => void
  onSubmit: (value: { label: string | null; url: string }) => void
  placeholderText?: string
  /**
   * `readOnly` renders a text only display the input value.
   * `disabled` prop renders popover component with button
   * in disabled state
   */
  readOnly?: boolean
}

export const URLInput: React.FC<URLInputProps> = ({
  className,
  disabled,
  htmlId,
  initialLabel,
  initialUrl,
  onCancel,
  onOpenChange,
  onSubmit,
  placeholderText,
  readOnly,
}) => {
  const theme = useTheme()
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState<string>(initialUrl ?? '')
  const [label, setLabel] = useState<string>(initialLabel ?? '')

  return (
    <div
      className={classNames(styles.URLInputStyleSelectors.root, className, {
        [styles.URLInputStyleStates.disabled]: disabled,
        [styles.URLInputStyleStates.hasValue]: Boolean(url || label),
      })}
      css={styles.urlInputContainer({
        expanded: open,
        theme,
        disablePopover: readOnly,
      })}
    >
      {readOnly ? (
        <span
          css={styles.readOnlyText({
            theme,
            showingPlaceholder: !label && !url,
          })}
        >
          {label || url || placeholderText}
        </span>
      ) : (
        <EditUrlPopover
          disabled={disabled}
          htmlId={htmlId}
          initialLabel={initialLabel}
          initialUrl={initialUrl}
          label={label}
          onCancel={onCancel}
          onOpenChange={onOpenChange}
          onSubmit={onSubmit}
          open={open}
          placeholderText={placeholderText}
          setOpen={setOpen}
          setLabel={setLabel}
          setUrl={setUrl}
          url={url}
        />
      )}
      {url && !disabled && <OpenLink url={url} />}
    </div>
  )
}
